'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';

export type FontSizeTier = 'normal' | 'large' | 'xlarge';

interface FontSizeContextType {
  fontSize: FontSizeTier;
  setFontSize: (size: FontSizeTier) => void;
}

const FontSizeContext = createContext<FontSizeContextType | undefined>(undefined);

export function FontSizeProvider({ children }: { children: React.ReactNode }) {
  const [fontSize, setFontSizeState] = useState<FontSizeTier>('normal');

  useEffect(() => {
    const saved = localStorage.getItem('school_saas_font_size') as FontSizeTier | null;
    if (saved && (saved === 'normal' || saved === 'large' || saved === 'xlarge')) {
      setFontSizeState(saved);
      document.documentElement.setAttribute('data-font-size', saved);
    } else {
      document.documentElement.setAttribute('data-font-size', 'normal');
    }
  }, []);

  const setFontSize = (size: FontSizeTier) => {
    setFontSizeState(size);
    localStorage.setItem('school_saas_font_size', size);
    document.documentElement.setAttribute('data-font-size', size);
  };

  return (
    <FontSizeContext.Provider value={{ fontSize, setFontSize }}>
      {children}
    </FontSizeContext.Provider>
  );
}

export function useFontSize() {
  const context = useContext(FontSizeContext);
  if (!context) {
    throw new Error('useFontSize must be used within a FontSizeProvider');
  }
  return context;
}
